import { useEffect, useCallback, useRef } from 'react';

/**
 * useMetaPixel - Hook for Meta (Facebook) Pixel tracking
 * Only loads and tracks if:
 * 1. Pixel ID is provided and pixel is enabled for the event
 * 2. User has accepted marketing cookies
 * 
 * Supports: PageView, ViewContent, InitiateCheckout, Purchase
 */
export const useMetaPixel = (pixelId, isEnabled = false) => {
  const pixelInitialized = useRef(false);
  const currentPixelId = useRef(null);

  // Check if marketing cookies are accepted
  const hasMarketingConsent = useCallback(() => {
    try {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        const parsed = JSON.parse(consent);
        return parsed.marketing === true;
      }
    } catch (e) {
      console.warn('Error checking cookie consent:', e);
    }
    return false;
  }, []);

  // Initialize Meta Pixel
  const initPixel = useCallback(() => {
    if (!pixelId || !isEnabled || pixelInitialized.current) return;
    if (!hasMarketingConsent()) {
      console.log('[MetaPixel] Marketing cookies not accepted, pixel not loaded');
      return;
    }

    // Don't reinitialize if same pixel
    if (currentPixelId.current === pixelId) return;

    console.log('[MetaPixel] Initializing pixel:', pixelId);

    // Load Facebook Pixel script
    if (!window.fbq) {
      (function(f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    // Initialize with the pixel ID
    window.fbq('init', pixelId);
    pixelInitialized.current = true;
    currentPixelId.current = pixelId;

    // Track initial PageView
    window.fbq('track', 'PageView');
    console.log('[MetaPixel] PageView tracked for pixel:', pixelId);
  }, [pixelId, isEnabled, hasMarketingConsent]);

  // Track PageView
  const trackPageView = useCallback(() => {
    if (!pixelId || !isEnabled || !hasMarketingConsent()) return;
    if (!window.fbq) {
      initPixel();
      return;
    }
    window.fbq('track', 'PageView');
    console.log('[MetaPixel] PageView tracked');
  }, [pixelId, isEnabled, hasMarketingConsent, initPixel]);

  // Track ViewContent (when viewing event details)
  const trackViewContent = useCallback((contentData = {}) => {
    if (!pixelId || !isEnabled || !hasMarketingConsent() || !window.fbq) return;
    
    window.fbq('track', 'ViewContent', {
      content_type: 'product',
      content_ids: [contentData.content_id || ''],
      content_name: contentData.content_name || '',
      content_category: contentData.content_category || '',
      value: contentData.value || 0,
      currency: contentData.currency || 'EUR'
    });
    console.log('[MetaPixel] ViewContent tracked:', contentData.content_name);
  }, [pixelId, isEnabled, hasMarketingConsent]);

  // Track InitiateCheckout (when clicking buy button)
  const trackInitiateCheckout = useCallback((checkoutData = {}) => {
    if (!pixelId || !isEnabled || !hasMarketingConsent() || !window.fbq) return;
    
    window.fbq('track', 'InitiateCheckout', {
      content_ids: checkoutData.content_ids || [],
      content_name: checkoutData.content_name || '',
      content_category: checkoutData.content_category || '',
      value: checkoutData.value || 0,
      currency: checkoutData.currency || 'EUR',
      num_items: checkoutData.num_items || 1
    });
    console.log('[MetaPixel] InitiateCheckout tracked:', checkoutData.content_name);
  }, [pixelId, isEnabled, hasMarketingConsent]);

  // Track Purchase (after successful purchase)
  const trackPurchase = useCallback((purchaseData = {}) => {
    if (!pixelId || !isEnabled || !hasMarketingConsent() || !window.fbq) return;
    
    window.fbq('track', 'Purchase', {
      content_ids: purchaseData.content_ids || [],
      content_name: purchaseData.content_name || '',
      content_type: 'product',
      value: purchaseData.value || 0,
      currency: purchaseData.currency || 'EUR',
      num_items: purchaseData.num_items || 1
    });
    console.log('[MetaPixel] Purchase tracked:', purchaseData.content_name);
  }, [pixelId, isEnabled, hasMarketingConsent]);

  // Initialize pixel on mount if conditions are met
  useEffect(() => {
    if (pixelId && isEnabled && hasMarketingConsent()) {
      initPixel();
    }
  }, [pixelId, isEnabled, initPixel, hasMarketingConsent]);

  // Listen for cookie consent changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent' && pixelId && isEnabled) {
        if (hasMarketingConsent()) {
          initPixel();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [pixelId, isEnabled, initPixel, hasMarketingConsent]);

  return {
    trackPageView,
    trackViewContent,
    trackInitiateCheckout,
    trackPurchase,
    isActive: pixelId && isEnabled && hasMarketingConsent() && pixelInitialized.current
  };
};

export default useMetaPixel;
